import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiService from "services/ApiService";
import { signOut } from "./authSlice";

export const initialState = {
  customers: [],
  bcCategories: [],
  integrators: [],
  orderEmails: [],
  customerGroups: [],
  termsOfPayment: [],
  chargesGroup: [],
  countries: [],
  cities: [],
  states: [],
  counties: [],
  postalCodes: [],
  postalAddresses: [],
  loading: false,
  message: "",
  showMessage: false,
  success: true,
  totalRecords: 0,
  offset: 0,
  limit: 10,
  search: "",
};

export const fetchCustomers = createAsyncThunk(
  "customer/fetchCustomers",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomers();
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching customers.",
      );
    }
  },
);

export const fetchCustomerPricingFlag = createAsyncThunk(
  "customer/fetchCustomerPricingFlag",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomerPricingFlag(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching customer pricing flag.",
      );
    }
  },
);

export const fetchCustomersWithPagination = createAsyncThunk(
  "customer/fetchCustomersWithPagination",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomersWithPagination(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching customers.",
      );
    }
  },
);

export const fetchBCCategories = createAsyncThunk(
  "customer/fetchBCCategories",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getBCCategories();
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching Big Commerce Categories.",
      );
    }
  },
);

export const fetchIntegrators = createAsyncThunk(
  "customer/fetchIntegrators",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getIntegrators();
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching integrators.",
      );
    }
  },
);

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.addCustomer(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while adding the customer.",
      );
    }
  },
);

export const importCustomer = createAsyncThunk(
  "customer/importCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.importD365Customer(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while importing the D365 customer.",
      );
    }
  },
);

export const migrateCustomer = createAsyncThunk(
  "customer/migrateCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.migrateCustomer(data);
      if (response.status === 200) {
        return response.data;
      }
      return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response?.data ||
          err.response ||
          "Something went wrong while migrating the customer.",
      );
    }
  },
);

export const addD365Customer = createAsyncThunk(
  "customer/addD365Customer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.addD365Customer(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while adding the D365 customer.",
      );
    }
  },
);

export const addD365IntegratorCustomer = createAsyncThunk(
  "customer/addD365IntegratorCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.addD365Customer(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while adding the D365 customer.",
      );
    }
  },
);

export const emailCheck = createAsyncThunk(
  "customer/emailCheck",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.emailCheck(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while checking the email.",
      );
    }
  },
);

export const addExternalOperatorMapping = createAsyncThunk(
  "customer/addExternalOperatorMapping",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.addExternalOperatorMapping(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while adding the D365 customer.",
      );
    }
  },
);

export const validatePartnerAndOperator = createAsyncThunk(
  "customer/validatePartnerAndOperator",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.validatePartnerAndOperator(data);

      // 🚨 HANDLE NON-200 HERE
      if (response.status !== 200) {
        return rejectWithValue({
          status: response.status,
          data: response.data,
          message: "Invalid partner or token",
        });
      }

      return response.data;
    } catch (err) {
      return rejectWithValue({
        status: err?.response?.status || 500,
        data: err?.response?.data || null,
        message:
          err?.response?.data?.message ||
          err.message ||
          "Something went wrong while validating the partner and operator.",
      });
    }
  },
);

export const editCustomer = createAsyncThunk(
  "customer/editCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.editCustomer(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while editing the customer.",
      );
    }
  },
);

export const updateCustomerCategory = createAsyncThunk(
  "customer/updateCustomerCategory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerCategory(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer category.",
      );
    }
  },
);

export const updateCustomerStatus = createAsyncThunk(
  "customer/updateCustomerStatus",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerStatus(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer status.",
      );
    }
  },
);

export const updateReapprovedCustomerStatus = createAsyncThunk(
  "customer/updateReapprovedCustomerStatus",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerStatus(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer reapproved status.",
      );
    }
  },
);

export const updateCustomerShowPricingFlag = createAsyncThunk(
  "customer/updateCustomerShowPricingFlag",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerPricing(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer show pricing flag.",
      );
    }
  },
);

export const updateCustomerIntegrator = createAsyncThunk(
  "customer/updateCustomerIntegrator",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerIntegrator(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer integrator.",
      );
    }
  },
);

export const updateCustomerOrderEmails = createAsyncThunk(
  "customer/updateCustomerOrderEmails",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.updateCustomerOrderEmails(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while updating the customer order emails.",
      );
    }
  },
);

export const deleteCustomer = createAsyncThunk(
  "customer/deleteCustomer",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.deleteCustomer(data);
      if (response.status === 200) {
        return {
          success: response.data.success,
          message: response.data.message,
          customerId: data.customerId,
        };
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while deleting the customer.",
      );
    }
  },
);

export const fetchCustomerDetailsByOktaUserId = createAsyncThunk(
  "customer/fetchCustomerDetailsByOktaUserId",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomerByOktaId(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching customer details.",
      );
    }
  },
);

export const fetchCustomerFacets = createAsyncThunk(
  "customer/fetchCustomerFacets",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomerFacets(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching customer facets.",
      );
    }
  },
);

export const getD365CustomerDetails = createAsyncThunk(
  "customer/getD365CustomerDetails",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getD365CustomerDetails(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching D365 customer details.",
      );
    }
  },
);

export const fetchCustomerIntegrators = createAsyncThunk(
  "customer/fetchCustomerIntegrators",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomerIntegrators(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching customer integrators.",
      );
    }
  },
);

export const fetchCustomerOrderEmails = createAsyncThunk(
  "customer/fetchCustomerOrderEmails",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getCustomerOrderEmails(data);
      if (response.status === 200) {
        return response.data;
      } else return rejectWithValue(response.data);
    } catch (err) {
      return rejectWithValue(
        err.response ||
          "Something went wrong while fetching customer order emails.",
      );
    }
  },
);

export const fetchPostalAddresses = createAsyncThunk(
  "customer/fetchPostalAddresses",
  async (data, { rejectWithValue }) => {
    try {
      const response = await ApiService.getPostalAddresses(data);
      if (response.status === 200) {
        return {
          postalAddresses: response.data.data.value,
          cities: response.data.data["@search.facets"].city.map(
            ({ value }) => value,
          ),
          states: response.data.data["@search.facets"].stateDescription.map(
            ({ value }) => value,
          ),
          counties: response.data.data["@search.facets"].countyDescription.map(
            ({ value }) => value,
          ),
          postalCodes: response.data.data["@search.facets"].zipCode.map(
            ({ value }) => value,
          ),
          data,
        };
      } else if (response.status === 401) {
        return rejectWithValue(response.data);
      } else return initialState;
    } catch (err) {
      return rejectWithValue(
        err.response || "Something went wrong while fetching postal addresses.",
      );
    }
  },
);

export const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    clearMessage: (state, action) => {
      state.message = null;
    },
    setMessage: (state, action) => {
      state.success = false;
      state.message =
        "More than one Party ID needs to be associated with a grouping customer";
    },
    setSearch: (state, action) => {
      state.search = action.payload;
    },
    setSelectedCustomerType: (state, action) => {
      state.selectedCustomerType = action.payload;
    },
    setCustomerEnabledStatus: (state, action) => {
      state.customerEnabledStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          customers: data.sort((a, b) => a.name.localeCompare(b.name)),
          loading: false,
        };
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(fetchCustomerPricingFlag.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomerPricingFlag.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchCustomerPricingFlag.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(fetchCustomersWithPagination.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomersWithPagination.fulfilled, (state, action) => {
        const { data } = action.payload;
        const { customers, totalRecords, offset, limit } = data;
        return {
          ...state,
          customers,
          totalRecords,
          offset,
          limit,
          loading: false,
        };
      })
      .addCase(fetchCustomersWithPagination.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchBCCategories.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchBCCategories.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          bcCategories: data,
          loading: false,
        };
      })
      .addCase(fetchBCCategories.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchIntegrators.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchIntegrators.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          integrators: data,
          loading: false,
        };
      })
      .addCase(fetchIntegrators.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchCustomerIntegrators.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomerIntegrators.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          integrators: data,
          loading: false,
        };
      })
      .addCase(fetchCustomerIntegrators.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchCustomerFacets.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomerFacets.fulfilled, (state, action) => {
        const { data } = action.payload;
        const {
          customerGroups,
          chargesGroups,
          termsofPayments,
          bcCategories,
          countries,
          kitchenContacts,
        } = data;
        return {
          ...state,
          customerGroups: customerGroups,
          chargesGroup: chargesGroups,
          termsOfPayment: termsofPayments,
          bcCategories: bcCategories,
          countries: countries,
          kitchenContacts: kitchenContacts,
          loading: false,
        };
      })
      .addCase(fetchCustomerFacets.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchPostalAddresses.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchPostalAddresses.fulfilled, (state, action) => {
        const { cities, states, counties, postalCodes, postalAddresses, data } =
          action.payload;
        let updatedObject = { ...state, postalAddresses, loading: false };
        if (!data.state) {
          updatedObject = {
            ...updatedObject,
            states,
          };
        }
        if (!data.city) {
          updatedObject = {
            ...updatedObject,
            cities,
          };
        }
        if (!data.postalCode) {
          updatedObject = {
            ...updatedObject,
            postalCodes,
          };
        }
        if (!data.county) {
          updatedObject = {
            ...updatedObject,
            counties,
          };
        }
        return updatedObject;
      })
      .addCase(fetchPostalAddresses.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(addCustomer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          customers: [...state.customers, data],
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(addCustomer.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(importCustomer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(importCustomer.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          customers: [...state.customers, data],
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(importCustomer.rejected, (state, action) => {
        const { message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: false,
        };
      })
      .addCase(migrateCustomer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(migrateCustomer.fulfilled, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message,
          success,
        };
      })
      .addCase(migrateCustomer.rejected, (state, action) => {
        const { message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: false,
        };
      })
      .addCase(addD365Customer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(addD365Customer.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          customers: [...state.customers, data],
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(addD365Customer.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(addD365IntegratorCustomer.pending, (state) => {
        return { ...state, loading: false };
      })
      .addCase(addD365IntegratorCustomer.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          customers: [...state.customers, data],
          loading: false,
          // message: message,
          success: success,
        };
      })
      .addCase(addD365IntegratorCustomer.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(addExternalOperatorMapping.pending, (state) => {
        return { ...state, loading: false };
      })
      .addCase(addExternalOperatorMapping.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        return {
          ...state,
          loading: false,
          // message: message,
          success: success,
        };
      })
      .addCase(addExternalOperatorMapping.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(getD365CustomerDetails.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(getD365CustomerDetails.fulfilled, (state, action) => {
        const { success } = action.payload;
        return {
          ...state,
          loading: false,
          // message: message,
          success: success,
        };
      })
      .addCase(getD365CustomerDetails.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(editCustomer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { id } = data;
        const editedCustomers = state.customers.map((a) => {
          if (a.id === id) {
            return data;
          } else {
            return a;
          }
        });
        return {
          ...state,
          customers: editedCustomers,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(editCustomer.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerCategory.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateCustomerCategory.fulfilled, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerCategory.rejected, (state, action) => {
        const { success, message } = action.payload;

        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateCustomerStatus.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { id, status } = data;
        const selectedCustomerApproval = state.customerApprovals.find(
          (a) => a.id === id,
        );

        return {
          ...state,
          customers: state.customers.map((customer) =>
            customer.id === selectedCustomerApproval?.id
              ? {
                  ...customer,
                  status,
                  showPricing: status === 1,
                }
              : customer,
          ),
          customerApprovals: state.customerApprovals.filter((a) => a.id !== id),
          loading: false,
          message,
          success,
        };
      })
      .addCase(updateCustomerStatus.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateReapprovedCustomerStatus.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateReapprovedCustomerStatus.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { id, status } = data;
        const selectedCustomerApproval = state.deniedCustomers.find(
          (a) => a.id === id,
        );

        return {
          ...state,
          customers: state.customers.map((customer) =>
            customer.id === selectedCustomerApproval?.id
              ? {
                  ...customer,
                  status,
                  showPricing: status === 1,
                }
              : customer,
          ),
          deniedCustomers: state.deniedCustomers.filter((a) => a.id !== id),
          loading: false,
          message,
          success,
        };
      })
      .addCase(updateReapprovedCustomerStatus.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerShowPricingFlag.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateCustomerShowPricingFlag.fulfilled, (state, action) => {
        const { success, message, data } = action.payload;
        const { id, showPricing } = data;
        const editedCustomer = state.customers.map((a) => {
          if (a.id === id) {
            return { ...a, showPricing: showPricing };
          } else {
            return a;
          }
        });
        return {
          ...state,
          customers: editedCustomer,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerShowPricingFlag.rejected, (state, action) => {
        const { success, message } = action.payload;

        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerIntegrator.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateCustomerIntegrator.fulfilled, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerIntegrator.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerOrderEmails.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(updateCustomerOrderEmails.fulfilled, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(updateCustomerOrderEmails.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(deleteCustomer.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        const { success, message, customerId } = action.payload;
        return {
          ...state,
          customers: state.customers.filter((a) => a.id !== customerId),
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        const { success, message } = action.payload;
        return {
          ...state,
          loading: false,
          message: message,
          success: success,
        };
      })
      .addCase(fetchCustomerDetailsByOktaUserId.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomerDetailsByOktaUserId.fulfilled, (state, action) => {
        // const { data } = action.payload;
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(fetchCustomerDetailsByOktaUserId.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(emailCheck.pending, (state) => {
        return { ...state, loading: false };
      })
      .addCase(emailCheck.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
        };
      })
      .addCase(emailCheck.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(fetchCustomerOrderEmails.pending, (state) => {
        return { ...state, loading: true };
      })
      .addCase(fetchCustomerOrderEmails.fulfilled, (state, action) => {
        const { data } = action.payload;
        return {
          ...state,
          orderEmails: data,
          loading: false,
        };
      })
      .addCase(fetchCustomerOrderEmails.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          message: action.payload,
          success: false,
        };
      })
      .addCase(signOut.fulfilled, (state) => {
        return initialState;
      });
  },
});

export const {
  clearMessage,
  setMessage,
  setSearch,
  setSelectedCustomerType,
  setCustomerEnabledStatus,
} = customerSlice.actions;

export default customerSlice.reducer;
